// FIN-865: set as the Supabase client's `global.fetch` (src/integrations/supabase/client.ts) —
// supabase-js passes that same custom fetch to both its PostgREST client (rest/v1) and its
// Functions client (functions/v1), so this one wrapper covers `supabase.from(...)` and
// `supabase.functions.invoke(...)` alike without touching any of the 33 API clients under
// src/clients/ individually (see specs/in-app-feedback/plan.md's design table — "Editing 33 API
// clients: prone to drift").
//
// Deliberately lives in src/clients/ (not src/lib/) so corsHeaderCoverage.test.ts — which scans
// every src/clients/*.ts file for a custom `x-*` header literal — includes it; the header names
// below are already present in supabase/functions/_shared/utils/errorHandler.ts's CORS
// allowlist (FIN-864/PR1), so that coverage test stays green without any further change.
import { getSessionId, recordCorrelationId } from '@/lib/correlationStore';
import { setSentryCorrelationTags } from '@/lib/sentry';

export const CORRELATION_HEADER = 'x-harmony-correlation-id';
export const SESSION_HEADER = 'x-harmony-session-id';

// Only these two backend surfaces understand/echo the header — scoping the mint+header-set to
// them keeps Realtime/Auth/Storage traffic (which also flows through this same custom fetch,
// since supabase-js has no narrower hook) untouched.
const CORRELATED_PATH_RE = /\/(functions|rest)\/v1\//;

function shouldCorrelate(input: RequestInfo | URL): boolean {
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.href;
  } else {
    url = input.url;
  }
  return CORRELATED_PATH_RE.test(url);
}

/**
 * A `Fetch`-compatible function: mints a UUIDv4 per correlated request, attaches it (plus the
 * tab's session id) as headers, records it into the ring buffer (correlationStore.ts, read by
 * FIN-550's feedback capture) and tags the current Sentry scope — then calls the real fetch
 * unchanged for everything else.
 */
export const correlatedFetch: typeof fetch = (input, init) => {
  if (!shouldCorrelate(input)) {
    return fetch(input, init);
  }

  const correlationId = crypto.randomUUID();
  const sessionId = getSessionId();
  recordCorrelationId(correlationId);
  setSentryCorrelationTags(correlationId, sessionId);

  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set(CORRELATION_HEADER, correlationId);
  headers.set(SESSION_HEADER, sessionId);

  return fetch(input, { ...init, headers });
};
